/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component} from '@angular/core';
import {MatDialog} from "@angular/material/dialog";
import {FabMessageService} from "@fabric/components/message";
import {VEHICLE_PROPULSION_COLDEF_NO_FILTER} from '../../../common/constants';
import {InfoPopUpComponent} from "../../../core/components/pop-ups/info-pop-up/info-pop-up.component";
import {SelectorPopUpComponent} from "../../../core/components/pop-ups/selector-pop-up/selector-pop-up.component";
import {CustomDataDisplay} from "../../../core/models/custom-data-display.model";
import {StateService} from "../../../core/state/state.service";
import {VehicleScenario} from "../../../models/vehicle-scenario-dto.model";
import {PropulsionScenarioService} from "../../../services/propulsion-scenario-service";
import {VehicleScenarioService} from "../../../services/vehicle-scenario.service";
import {BulkPropScenUpdatePopUpComponent} from "./bulk-prop-scen-update-pop-up/bulk-prop-scen-update-pop-up.component";

@Component({
    selector: 'app-bulk-update-to-portfolio',
    templateUrl: './bulk-update-to-portfolio.component.html',
    styleUrl: './bulk-update-to-portfolio.component.scss',
    standalone: false
})
export class BulkUpdateToPortfolioComponent {
  protected readonly VEHICLE_PROPULSION_COLDEF_NO_FILTER = VEHICLE_PROPULSION_COLDEF_NO_FILTER;

  showSpinner: boolean;
  memberScenarios: VehicleScenario[] = [];

  constructor(public snackBar: FabMessageService,
              readonly stateService: StateService,
              readonly vehicleScenarioService: VehicleScenarioService,
              readonly propulsionScenarioService: PropulsionScenarioService,
              readonly dialog: MatDialog) {
  }

  /**
   * Event handler for whenever a global scenario is selected from the table
   * @param data the data that is selected
   */
  onSelect(data): void {
    this.memberScenarios = data.memberScenarios;
  }

  /**
   * Bulk update all selected vehicle scenarios with the vehicle portfolio
   */
  async bulkUpdateWithVehiclePortfolio(): Promise<void> {
    const successfulUpdates = [];
    const failedUpdates = [];
    await this.dialog.open(SelectorPopUpComponent, {
      data: {
        customDataDisplay: this.memberScenarios.map(scenario => {
          const customDataDisplay: CustomDataDisplay = {
            data: scenario,
            displayValue: scenario.vehScenId + ' ' + scenario.vehScenName
          };
          return customDataDisplay;
        }),
        isMultiSelect: true,
        headerText: 'Select Vehicle Scenario(s) to update with Vehicle Portfolio:'
      },
      disableClose: true,
      width: '500px'
    }).afterClosed().toPromise().then(async (selectedScenarios: VehicleScenario[]) => {
      if (selectedScenarios) {
        // Loop through each selected scenario and update it with the vehicle portfolio
        for (const scenario of selectedScenarios) {
          const isSuccess = await this.updateWithVehiclePortfolio(scenario, this.stateService.getUserGmid());
          if (isSuccess) {
            successfulUpdates.push(scenario);
          } else {
            failedUpdates.push(scenario);
          }
        }
        this.displaySummaryMessage(successfulUpdates, failedUpdates, true);
      }
    });
  }

  /**
   * Bulk update all selected propulsion scenarios with the propulsion portfolio
   */
  async bulkUpdateWithPropulsionPortfolio(): Promise<void> {
    const successfulUpdates = [];
    const failedUpdates = [];
    await this.dialog.open(BulkPropScenUpdatePopUpComponent, {
      data: {
        scenarios: this.memberScenarios
        .filter(scenario => scenario.propulsionScenario.id !== null)
      },
      disableClose: true,
      width: '700px'
    }).afterClosed().toPromise().then(async (data: any) => {
      // Loop through each selected scenario and update it with the propulsion portfolio
      for (const scenario of data.selectedOptions) {
        const isSuccess = await this.updateWithPropulsionPortfolio(scenario, this.stateService.getUserGmid(), data.isRemoveUnmatched,
          data.selectedStartYear, data.selectedEndYear);
        if (isSuccess) {
          successfulUpdates.push(scenario);
        } else {
          failedUpdates.push(scenario);
        }
      }
      this.displaySummaryMessage(successfulUpdates, failedUpdates, false);
    });
  }

  /**
   * Make service call to update a vehicle scenario with the vehicle portfolio
   * @param vehScen the vehicle scenario  to update
   * @param gmid the id of the user
   */
  async updateWithVehiclePortfolio(vehScen: VehicleScenario, gmid: string): Promise<boolean> {
    this.showSpinner = true;
    return this.vehicleScenarioService
    .updateVehicleScenarioWithPortfolio(vehScen.vehScenId,
      gmid, '', '', '', '', '', '').toPromise().then(() => {
      this.showSpinner = false;
      return true;
    }).catch(() => {
      this.showSpinner = false;
      return false;
    });
  }

  /**
   * Make service call to update a propulsion scenario with the propulsion portfolio
   * @param vehScen the vehicle scenario that holds the propulsion scenario you want to update
   * @param gmid the id of the user
   * @param isYearRange whether to update a range of years
   * @param startYear the start year
   * @param endYear the end year
   */
  async updateWithPropulsionPortfolio(vehScen: VehicleScenario, gmid: string, isYearRange: boolean,
                                      startYear: number, endYear: number): Promise<boolean> {
    this.showSpinner = true;
    return this.propulsionScenarioService
    .updatePropulsionScenarioWithPortfolio(vehScen.propulsionScenario.id,
      vehScen.vehScenId, isYearRange, gmid, startYear, endYear).toPromise().then(() => {
      this.showSpinner = false;
      return true;
    }).catch(() => {
      this.showSpinner = false;
      return false;
    });
  }

  /**
   * Display a summary message of the successful and failed updates
   * @param successfulUpdates the scenarios that were successfully updated
   * @param failedUpdates the scenarios that failed to update
   * @param isVehicleUpdate whether the update was for a vehicle scenario or propulsion scenario
   */
  private displaySummaryMessage(successfulUpdates: VehicleScenario[], failedUpdates: VehicleScenario[],
                                isVehicleUpdate: boolean): void {
    let message = '';
    const failedMessage = isVehicleUpdate ?
      failedUpdates.map(scenario => scenario.vehScenId + ' ' + scenario.vehScenName).join(', ') :
      failedUpdates.map(scenario => scenario.propulsionScenario.id + ' '
        + scenario.propulsionScenario.propulsionScenarioName).join(', ');
    const successMessage = isVehicleUpdate ?
      successfulUpdates.map(scenario => scenario.vehScenId + ' ' + scenario.vehScenName).join(', ') :
      successfulUpdates.map(scenario => scenario.propulsionScenario.id + ' '
        + scenario.propulsionScenario.propulsionScenarioName).join(', ');
    if (failedUpdates.length > 0) {
      message += 'The following scenarios failed to update: ' + failedMessage
        + '\nPlease check if user has right access or if scenarios are edit locked.\n\n';
    }
    if (successfulUpdates.length > 0) {
      message += 'The following scenarios were successfully updated: ' + successMessage + '\n';
    }
    this.dialog.open(InfoPopUpComponent, {
      width: '500px',
      data: {
        title: failedUpdates.length > 0 ? 'Error' : 'Successful update',
        info: message
      },
      disableClose: true
    });
  }

}
